import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, ChevronRight } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { DOCS, DOC_GROUPS, findDoc } from "@/content/docs";
import { DOCS as DOCS_TID } from "@/constants/testIds";

function Block({ block }) {
  switch (block.type) {
    case "h":
      return <h2 className="mt-8 text-lg font-semibold tracking-tight text-foreground">{block.text}</h2>;
    case "p":
      return <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{block.text}</p>;
    case "ul":
      return (
        <ul className="mt-3 space-y-1.5">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-2 text-sm text-muted-foreground">
              <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="mt-3 space-y-1.5">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-2 text-sm text-muted-foreground">
              <span className="text-accent">{i + 1}.</span>
              <span>{it}</span>
            </li>
          ))}
        </ol>
      );
    case "code":
      return (
        <pre className="mt-3 overflow-x-auto rounded-lg border border-border/60 bg-card/60 px-4 py-3 text-xs text-accent">
          <code>{block.text}</code>
        </pre>
      );
    case "note":
      return (
        <div className="mt-4 rounded-lg border border-accent/40 bg-accent/5 px-4 py-3 text-xs text-foreground">
          {block.text}
        </div>
      );
    default:
      return null;
  }
}

export default function Docs() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const doc = findDoc(slug);

  return (
    <div data-testid={DOCS_TID.root} className="min-h-screen bg-background text-foreground">
      <SeoHead
        title={slug && doc ? `${doc.title} — Sema Docs` : "Documentation — Guides & Reference | Sema"}
        description={
          slug && doc
            ? `Sema documentation: ${doc.title}. Step-by-step guidance for the Sema semantic layer.`
            : "Guides for connecting sources, building your glossary, conversational query, reports, alerts, RBAC and security in Sema."
        }
        path={slug && doc ? `/docs/${doc.slug}` : "/docs"}
      />
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-6 py-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" />
            <span className="font-semibold">sema</span>
            <span className="text-muted-foreground">/ docs</span>
          </Link>
          <button
            data-testid={DOCS_TID.backToApp}
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to app
          </button>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-6xl gap-8 px-6 py-8">
        {/* Sidebar */}
        <aside data-testid={DOCS_TID.sidebar} className="hidden w-60 shrink-0 md:block">
          <div className="sticky top-20 space-y-6">
            {Object.entries(DOC_GROUPS).map(([group, items]) => (
              <div key={group}>
                <p className="px-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{group}</p>
                <nav className="mt-2 space-y-0.5">
                  {items.map((d) => {
                    const active = d.slug === doc.slug;
                    return (
                      <Link
                        key={d.slug}
                        to={`/docs/${d.slug}`}
                        data-testid={`docs-link-${d.slug}`}
                        className={`flex items-center justify-between rounded-lg px-2 py-1.5 text-xs transition-colors ${active ? "bg-accent/10 text-accent" : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"}`}
                      >
                        {d.title}
                        {active && <ChevronRight className="h-3.5 w-3.5" />}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            ))}
          </div>
        </aside>

        {/* Content */}
        <article data-testid={DOCS_TID.content} className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-accent">
            <BookOpen className="h-3.5 w-3.5" /> {doc.group}
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight">{doc.title}</h1>
          <div className="mt-2 max-w-2xl">
            {doc.blocks.map((blk, i) => <Block key={i} block={blk} />)}
          </div>

          {/* mobile nav */}
          <div className="mt-10 border-t border-border/50 pt-6 md:hidden">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">All docs</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {DOCS.map((d) => (
                <Link key={d.slug} to={`/docs/${d.slug}`} className="rounded-full border border-border/60 px-3 py-1 text-[11px] text-muted-foreground">
                  {d.title}
                </Link>
              ))}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
